import React, { useContext, useEffect, useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { CarecoApiContext } from '@/app-context/careco-api-context';
import Button, { StyleType } from "patient-ping-remedy/packages/button";
import Icon from "patient-ping-remedy/packages/icon";
import IncompleteCRFsTable from "@/components/pages/analytics/IncompleteCRFsTable";
import { FiltersRow } from "@/components/pages/analytics/FilterDropDown";
import {
  TableContainer, CountBox, CountNumber, CountTopLabel,
  CountBottomLabel, CountBoxContainer, StyledDisplayLargeTypography, FiltersWrapper,
  HeaderWithExportContainer, FiltersAndTabsContainer, ExportButtonWrapper
}
  from '@/components/pages/analytics/IncompleteCRFReport.styles';
import Spacer from "patient-ping-remedy/packages/spacer";
import DisplayHelpers from "@/helpers/display_helpers";
import { css } from "@emotion/css";
import { colors, fontSize } from "patient-ping-remedy/packages/theme";
import { nonGroupedValues } from "@common/FilterComponent";
import { TableColumn } from "@common/SortableTable";
import { SortOrderType } from "@clients/ClientsPage";
import FileHelpers from "@/helpers/file_helpers";
import { log } from "@/helpers/logger";
import ExportModal from "@/components/pages/analytics/ExportModal";

export default function IncompleteAdmissionCRFsReport() {
  const { carecoApi } = useContext(CarecoApiContext);
  const [searchTerm, setSearchTerm] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const [filters, setFilters] = useState<{
    rosterIds: string[],
    assigneeIds: string[],
    formTypes: string[],
    crfCompleted: string[],
    sortField?: string,
    sortDirection?: string,
  }>({
    rosterIds: [],
    assigneeIds: [],
    formTypes: [],
    crfCompleted: [],
  });

  const { data: rosters } = useQuery({
    queryKey: ['rosters'],
    queryFn: () => carecoApi?.getRosters(),
    enabled: !!carecoApi
  });

  const { data: assigneeOptions, isLoading: assigneeLoading } = useQuery({
    queryKey: ['assignedCrfReportOptions'],
    queryFn: () => carecoApi?.getUsersAssignedToClients().then(users => [
      {
        id: 'Unassigned',
        value: 'Unassigned',
        label: 'Unassigned',
      },
      ...users.map((user): nonGroupedValues => ({
        id: user.id,
        value: user.id,
        label: `${user.first_name} ${user.last_name}`,
      })),
    ]),
    enabled: !!carecoApi,
  });

  const { data: admissionClientsData, isLoading: admissionLoading, error: admissionError,
    isFetching: admitFetching, fetchNextPage } = useInfiniteQuery({
    queryKey: ['incompleteAdmissionCRFs', filters],
    refetchOnWindowFocus: false,
    staleTime: 0,
    queryFn: ({ pageParam }) => carecoApi?.getIncompleteAdmissionCRFs(
      filters.rosterIds,
      filters.assigneeIds,
      filters.crfCompleted,
      filters.sortField,
      filters.sortDirection,
      pageParam,
      25,
    ),
    enabled: !!carecoApi,
    getNextPageParam: (lastPage) => lastPage?.nextPage,
    initialPageParam: 1,
  });

  const admissionClients = admissionClientsData?.pages
    .flatMap((page) => page?.data.incompleteAdmissionCrfs ?? []) ?? [];

  const [admissionCRFCount, setAdmissionCRFCount] = useState(0);

  useEffect(() => {
    if (admissionClientsData) {
      setAdmissionCRFCount(admissionClientsData?.pages[0]?.data.totalIncompleteAdmissionCount ?? 0);
    }
  }, [admissionClientsData]);

  const searchFunction = (term: string) => {
    setSearchTerm(term);
  };

  const sortFunction = (sortKey: TableColumn, sortOrder: SortOrderType) => {
    setFilters((filters) => ({
      ...filters,
      sortField: sortKey.key,
      sortDirection: sortOrder.value,
    }));
  };

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const data = await carecoApi?.getIncompleteAdmissionCRFs(
        filters.rosterIds, filters.assigneeIds, filters.crfCompleted, filters.sortField, filters.sortDirection);
      const dataToExport = data?.data.incompleteAdmissionCrfs ?? [];

      const admissionHeaders = [
        'Client name',
        'Client Id',
        'Group',
        'Client admission date',
        'No of Days since admission',
        'Assigned to',
        'CRF form',
        'CRF completed status'
      ];

      const rows = dataToExport.map(client => {
        const [firstName, middleName, lastName] = client.clientName.split(' ');
        const clientName = DisplayHelpers.formatName(firstName, middleName, lastName);
        const daysSinceAdmission = DisplayHelpers.daysSince(client.clientAdmissionDate);

        const assignedUsers = client.client.userAssignments
          ?.map(assignment => `${assignment.user.first_name} ${assignment.user.last_name}`)
          .join(', ') || 'Unassigned';

        const incompleteFormValue = client.contractedProvider ? 'Admission CRF' : 'N/A';
        const crfCompletedValue = client.contractedProvider
          ? (client.crfCompleted ? 'Yes' : 'No')
          : 'N/A';

        return [
          clientName,
          client.clientId,
          client.clientGroupAdmission,
          DisplayHelpers.formatDateFromDateOnlyString(client.clientAdmissionDate),
          daysSinceAdmission,
          assignedUsers,
          incompleteFormValue,
          crfCompletedValue
        ];
      });

      const csvContent = [
        admissionHeaders,
        ...rows,
      ]
        .map(row => row.map(cell => cell ? `"${cell}"` : '').join(','))
        .join('\n');

      const blob = new Blob([`\uFEFF${csvContent}`], { type: 'text/csv;charset=utf-16;' });
      const fileName = 'incomplete-admission-crfs.csv';

      FileHelpers.downloadFile(URL.createObjectURL(blob), fileName);
      setShowExportModal(false);
    } catch (error) {
      log('error', error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <>
      <div>
        <StyledDisplayLargeTypography>Admission CRF  <Spacer itemWidth={"2rem"} />
        </StyledDisplayLargeTypography>
        <span className={css({ fontWeight: 400, color: colors.gray5, fontSize: fontSize.displayLarge })}>
          {DisplayHelpers.formatDate(new Date())}</span>
        <div>
          <Spacer itemHeight={"1rem"} />
        </div>
        <HeaderWithExportContainer>
          <FiltersAndTabsContainer>
            <FiltersWrapper>
              <FiltersRow
                rosters={rosters}
                showIncompleteForm={false}
                showCRFCompleted={true}
                assigneeOptions={assigneeOptions}
                assigneeLoading={assigneeLoading}
                onFiltersChange={setFilters}
              />
            </FiltersWrapper>
          </FiltersAndTabsContainer>
          <ExportButtonWrapper>
            <Button
              styleType={StyleType.SECONDARY}
              disabled={isExporting}
              onClick={() => setShowExportModal(true)}
            >
              <Icon iconClass={'download'} style={{ marginRight: '10px' }} />
              Export
            </Button>
          </ExportButtonWrapper>
        </HeaderWithExportContainer>
        <FiltersWrapper>
          <CountBoxContainer>
            <CountBox>
              <CountTopLabel>CRF Forms</CountTopLabel>

              <CountNumber>{admissionCRFCount}</CountNumber>

              <CountBottomLabel>
                Admission CRF in total
              </CountBottomLabel>
            </CountBox>
          </CountBoxContainer>
        </FiltersWrapper>

        <TableContainer>
          <IncompleteCRFsTable
            clients={admissionClients}
            loading={admissionLoading}
            fetching={admitFetching}
            loadError={admissionError}
            searchTerm={searchTerm}
            searchFunction={searchFunction}
            sortFunction={sortFunction}
            onNextPage={fetchNextPage}
          />
        </TableContainer>
      </div>
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExport={handleExport}
        isExporting={isExporting}
      />
    </>
  );
}


